/* eslint-disable no-console */
import {
  createContext,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { decode } from '../../utils/base64Utils';
import { createDebugger } from '../debug/Debug';
import { dispatchImmediateAnalyticsEvent } from '../analytics/analyticsEvents';
import { MANAGED_IMPRESSION } from '../analytics/events';

// {FlagNameInCode: [flagNameInMonetate, defaultValue]}
import experienceSettings from './experienceSettings.json';
const { publicRuntimeConfig } = getConfig();
const { configDir } = publicRuntimeConfig;
let featuresConfig = '';
try {
  featuresConfig = require(`../../${configDir}featuresConfig.json`);
} catch (error) {
  console.error('--- configFile error ---', error);
}

const debug = createDebugger('monetate');

export const ExperiencesContext = createContext();

const ATB_BLOCKED_SKU_LIST = 'blockedATBList';

export const getExperiences = (experiences, featureFlagName) => {
  if (!experiences) {
    return [];
  }

  return experiences.filter((exp) => {
    const parameters = exp.parameters || {};
    return (
      Object.keys(parameters).includes(featureFlagName) &&
      parameters[featureFlagName] === 'on'
    );
  });
};

export const dispatchMonetateManagedImpression = ({
  monetateId = null,
  impressionIds = [],
}) => {
  if (
    impressionIds &&
    Array.isArray(impressionIds) &&
    impressionIds.length > 0
  ) {
    const monetateAPI =
      'https://engine.monetate.net/api/engine/v1/decide/JohnLewis';

    const impressionRecord = {
      channel:
        window.location.host === 'www.johnlewis.com'
          ? 'a-76ca7dd3/p/johnlewis.com'
          : 'a-76ca7dd3/d/dev-johnlewis.com',
      monetateId,
      events: [
        {
          eventType: 'monetate:record:Impressions',
          impressionIds,
        },
      ],
    };

    try {
      fetch(monetateAPI, {
        method: 'POST',
        body: JSON.stringify(impressionRecord),
      });
      debug(
        `posted to Monetate channel "${impressionRecord.channel}" with impressionIds "${impressionIds}"`
      );
    } catch (error) {
      console.error('dispatchMonetateManagedImpression', error);
    }
  }
};

export const dispatchMonetateManagedImpressions = ({
  monetateId,
  experiences,
  monetateFlagName,
}) => {
  const matchingExperiences = getExperiences(
    experiences,
    monetateFlagName
  ).filter((exp) => exp.impressionId);
  const impressionIds = matchingExperiences.map((exp) => exp.impressionId);
  if (impressionIds.length > 0) {
    try {
      const managedImpressionMonetateId = monetateId?.current;
      dispatchMonetateManagedImpression({
        monetateId: managedImpressionMonetateId,
        impressionIds,
      });
    } catch (error) {
      console.error(
        `Error dispatching Monetate managed impressions for ${monetateFlagName} and ${impressionIds}: ${JSON.stringify(
          error
        )}`
      );
    }
    dispatchImmediateAnalyticsEvent(MANAGED_IMPRESSION, {
      campaigns: matchingExperiences.map((exp) => ({
        id: exp.impressionId,
        key: exp.experienceName,
        reports: [],
        split: exp.variantLabel,
      })),
    });
  }
};

// get the experiences from the header (server side)
export const getExperiencesConfig = (req) => {
  const experiencesHeader = req?.headers?.['x-experiences-json'];
  if (!experiencesHeader) {
    return null;
  }

  return decode(experiencesHeader);
};

export const getFilteredExperiencesConfig = (
  experiencesConfig,
  serviceKeys
) => {
  return getFilteredExperiencesConfigByKey(
    experiencesConfig,
    'service',
    serviceKeys
  );
};

export const getFilteredExperiencesConfigByKey = (
  experiencesConfig,
  key,
  serviceKeys
) => {
  if (!experiencesConfig || !serviceKeys) {
    return null;
  }
  return JSON.parse(experiencesConfig).filter((experience) =>
    serviceKeys.includes(experience.parameters?.[key])
  );
};

// filter experiences by service (client side, after config has been parsed)
export const getFilteredExperiencesByKey = (experiences, key, serviceKeys) => {
  if (!experiences || !key || !serviceKeys) {
    return null;
  }

  return experiences.filter((experience) =>
    serviceKeys.includes(experience.parameters?.[key])
  );
};

export const useExperiencesContext = () => useContext(ExperiencesContext);

export const getMergedProperties = (experiences) => {
  if (!experiences) return [];
  let allParams = [];
  experiences.forEach((experience) => {
    const params = experience?.parameters;
    if (params) {
      const experienceParams = Object.keys(params).map((name) => {
        const alreadySetBy = allParams.find((param) => param.name === name);
        if (alreadySetBy) {
          // eslint-disable-next-line no-console
          console.debug(
            `${alreadySetBy.name} - ${alreadySetBy.value} : already set by : ${alreadySetBy.experienceName} skipping ${experience.experienceName}`
          );
          return undefined;
        }
        return {
          name,
          value: params[name],
          experienceId: experience.experienceId,
          experienceName: experience.experienceName,
          impressionId: experience.impressionId || 'null',
          variantLabel: experience.variantLabel,
        };
      });
      allParams = allParams.concat(experienceParams.filter(Boolean));
    }
  });
  return allParams;
};

export const getQueryConfigs = (query) => {
  const type = query?.type;
  if (type !== 'browse' && type !== 'search') {
    // Currently featuresConfig can only override feature flag default values for
    //   /browse pages for a whole dept
    //   /browse pages for an N-val
    //   /search pages
    // More development will be needed if we want to set up config for any other PLPs
    return {};
  }

  const departmentName = query.facetId?.[0];
  const facetIdArray = query.facetId;
  const nvals = facetIdArray?.[facetIdArray?.length - 1];

  const deptConfigs = featuresConfig?.queryConfigs?.filter(
    (config) =>
      config.query?.type !== 'search' &&
      !config.query?.nval &&
      config.query?.department === departmentName
  );

  const nvalConfigs = featuresConfig?.queryConfigs?.filter(
    (config) =>
      config.query?.type !== 'search' &&
      config.query?.nval &&
      nvals &&
      nvals.startsWith(config.query.nval)
  );

  const searchConfigs = featuresConfig?.queryConfigs?.filter(
    (config) => config.query?.type === 'search'
  );

  const featureSets = featuresConfig?.featureSets;

  return {
    deptConfigs: deptConfigs || [],
    nvalConfigs: nvalConfigs || [],
    searchConfigs: searchConfigs || [],
    featureSets: featureSets || {},
  };
};

export const getFeaturesFromConfig = (query) => {
  const queryConfigs = getQueryConfigs(query);
  const type = query?.type;

  let flags = {};

  if (type === 'browse') {
    queryConfigs?.deptConfigs?.forEach((config) => {
      config.featureSets?.forEach((featureSet) => {
        const flagsToAdd = featuresConfig?.featureSets[featureSet?.name];
        flags = { ...flags, ...flagsToAdd };
      });
    });
    // Flags from N-val config override flags from Dept config
    queryConfigs?.nvalConfigs?.forEach((config) => {
      config.featureSets?.forEach((featureSet) => {
        const flagsToAdd = featuresConfig?.featureSets[featureSet?.name];
        flags = { ...flags, ...flagsToAdd };
      });
    });
  }

  if (type === 'search') {
    queryConfigs?.searchConfigs?.forEach((config) => {
      config.featureSets?.forEach((featureSet) => {
        const flagsToAdd = featuresConfig?.featureSets[featureSet?.name];
        flags = { ...flags, ...flagsToAdd };
      });
    });
  }

  return flags;
};

export const getFeatureValue = (
  mergedProperties,
  featureName,
  defaultReturn = null
) => {
  const feature = mergedProperties.find(
    (property) => property.name === featureName
  );
  return (feature && feature.value) || defaultReturn;
};

export const getFlagValue = (
  mergedProperties,
  featureName,
  defaultReturn = true
) => {
  const featureValue = getFeatureValue(mergedProperties, featureName);
  return featureValue ? featureValue === 'on' : defaultReturn;
};

/**
 * Retrieves feature flag settings based on default values, experiences and optional query-driven config.
 *
 * This function takes two arguments and returns an object containing all feature
 * flag values.
 *
 * @param {Object} experiences - Experience data used to determine feature flags.
 * @param {Object} [query={}] - Optional object containing additional query
 *                              parameters. Used to check whether any query-driven config applies.
 * @returns {Object} An object containing all feature flag values, consolidated
 *                   from default values, overrides from the
 *                   query parameters and experience settings.
 */
export const getFlagSettings = (experiences, query = {}) => {
  const mergedProperties = getMergedProperties(experiences);

  const featuresFromConfig = getFeaturesFromConfig(query);

  // Merge in feature flag settings from experienceSettings, config and Monetate
  // Config overrides experienceSettings
  // Monetate overrides config settings
  const allFeatureFlagValues = Object.entries(experienceSettings).reduce(
    (acc, [flagNameInCode, [flagNameInMonetate, defaultValue = false]]) => {
      if (
        featuresFromConfig &&
        Object.prototype.hasOwnProperty.call(featuresFromConfig, flagNameInCode)
      ) {
        defaultValue = featuresFromConfig[flagNameInCode];
      }
      return {
        ...acc,
        [flagNameInCode]: getFlagValue(
          mergedProperties,
          flagNameInMonetate,
          defaultValue
        ),
      };
    },
    {}
  );
  return { ...allFeatureFlagValues };
};

export const getVariantLabel = ({ experiences, monetateFlagName }) => {
  const matchingExperiences = getExperiences(experiences, monetateFlagName).map(
    (exp) => exp.variantLabel
  );
  return matchingExperiences.length === 1 ? matchingExperiences[0] : '';
};

export const ExperiencesProvider = ({
  monetateId,
  children,
  experiencesConfig,
  rawExperiencesHeader,
}) => {
  const { query } = useRouter();
  const experiences = useMemo(
    () => (experiencesConfig ? JSON.parse(experiencesConfig) : null),
    [experiencesConfig]
  );
  const mergedProperties = useMemo(
    () => getMergedProperties(experiences),
    [experiences]
  );

  const queryConfigs = useMemo(() => getQueryConfigs(query), [query]);

  const getFeature = useCallback(
    (featureName, defaultReturn = null) =>
      getFeatureValue(mergedProperties, featureName, defaultReturn),
    [mergedProperties]
  );

  const contextValue = useMemo(() => {
    const flagSettings = getFlagSettings(experiences, query);

    const getImageOverwriteFeature = () => {
      const nValue = getFeature('nValue');
      const postfix = getFeature('postfix') || '';
      return (nValue && { nValue, postfix }) || undefined;
    };

    const unsanitizedAtbBlockList = getFeature(ATB_BLOCKED_SKU_LIST) || '';
    const atbBlockList = unsanitizedAtbBlockList.split(',');

    const imageOverwriteFeature = getImageOverwriteFeature();

    return {
      ...flagSettings,
      atbBlockList,
      experiences,
      imageOverwriteFeature,
      monetateId,
      rawExperiencesHeader,
    };
  }, [getFeature, monetateId, experiences, query]);

  const listFeatures = useCallback(() => {
    const tableLabel = `%c🚀 Monetate Feature Flags`;
    console.group(tableLabel, 'color:green; font-size: 1.2em');
    const keys = Object.keys(contextValue).filter((key) =>
      key.startsWith('is')
    );
    const features = keys.reduce(
      (obj, key) => ({ ...obj, [key]: contextValue[key] }),
      {}
    );

    const featureFlags = {};
    mergedProperties.forEach((property) => {
      featureFlags[`${property.name} [${property.value}]`] = {
        'Experience ID': property.experienceId,
        'Experience Name [Split]': `${property.experienceName} [${
          property.variantLabel || '100%'
        }]`,
        'Impression ID': property.impressionId,
      };
    });

    const printExperience = {};
    if (experiences) {
      experiences.forEach((experience) => {
        printExperience[`${experience.experienceName}`] = {
          'Experience ID': experience.experienceId,
          Split: experience.variantLabel || '100%',
          Parameters: `${
            experience.parameters ? JSON.stringify(experience.parameters) : ''
          }`,
          'Impression ID': experience.impressionId,
        };
      });
    }

    const printDeptConfigs = {};
    const printNvalConfigs = {};
    const { deptConfigs, nvalConfigs, featureSets } = queryConfigs;
    if (deptConfigs) {
      deptConfigs.forEach((config) => {
        printDeptConfigs[config.query.configName] = {
          type: config.query.type,
          dept: config.query.department,
          featureSets: config.featureSets.reduce(
            (names, curr) => names + `${curr.name} | `,
            ''
          ),
        };
      });
    }
    if (nvalConfigs) {
      nvalConfigs.forEach((config) => {
        printNvalConfigs[config.query.configName] = {
          type: config.query.type,
          nval: config.query.nval,
          featureSets: config.featureSets.reduce(
            (names, curr) => names + `${curr.name} | `,
            ''
          ),
        };
      });
    }

    const enabled = Object.values(features).filter(Boolean).length;
    let label = `${keys.length} feature flags.`;
    console.group(label);
    console.log(`${enabled} feature flags are enabled.`);
    console.log(`${keys.length - enabled} are disabled.`);
    if (Object.keys(features).length > 0) {
      console.table(features);
    } else {
      console.log('No features found');
    }
    console.groupEnd(label);

    label = `%c🧪 Experiments used to set flags`;
    console.group(label, 'color:orange; font-size: 1.2em');
    if (Object.keys(featureFlags).length > 0) {
      console.table(featureFlags);
    } else {
      console.log('No experiments found');
    }
    console.groupEnd(label);

    label = `%c🧪 All Experiments on this page`;
    console.group(label, 'color:turquoise; font-size: 1.2em');
    if (Object.keys(printExperience).length > 0) {
      console.table(printExperience);
    } else {
      console.log('No experiments found');
    }
    console.groupEnd(label);

    label = `%c🧪 Dept Config used to set flags`;
    console.group(label, 'color:turquoise; font-size: 1.2em');
    if (Object.keys(printDeptConfigs).length > 0) {
      console.table(printDeptConfigs);
    } else {
      console.log('No dept configs found');
    }
    console.groupEnd(label);

    label = `%c🧪 N-val Config used to set flags`;
    console.group(label, 'color:turquoise; font-size: 1.2em');
    if (Object.keys(printNvalConfigs).length > 0) {
      console.table(printNvalConfigs);
    } else {
      console.log('No facet configs found');
    }
    console.groupEnd(label);

    label = `%c🧪 Feature sets`;
    console.group(label, 'color:turquoise; font-size: 1.2em');
    if (featureSets && Object.keys(featureSets).length > 0) {
      console.table(featureSets);
    } else {
      console.log('No feature sets found');
    }
    console.groupEnd(label);

    console.groupEnd(tableLabel);

    return '';
  }, [contextValue, experiences, mergedProperties]);

  useEffect(() => {
    debug(`attaching "window.listFeatures()"`);
    window.listFeatures = listFeatures;
    return () => {
      delete window.listFeatures;
    };
  }, [listFeatures]);

  return (
    <ExperiencesContext.Provider value={contextValue}>
      {children}
    </ExperiencesContext.Provider>
  );
};
